import type { BlobCaster } from "./blob-shadows.ts";
import { SunShadow, SUN_SHADOW_SIZE } from "./sun-shadow.ts";
import { createMotes } from "./shaders/motes.ts";
import { MANDINGA } from "@eldrun/content";
import { sampleHeight } from "@eldrun/worldgen";
import { PerspectiveCamera, Scene, type DataTexture, type Mesh, type Points, type ShaderMaterial, type WebGLRenderer } from "three";
import { CAMERA_NEAR } from "../game/camera-rig.ts";
import { DetailLayer } from "../game/detail-layer.ts";
import { PoiLayer } from "../game/poi-layer.ts";
import { planBiomes, ResourceLayer } from "../game/resource-layer.ts";
import type { FrameStats } from "../engine/frame-stats.ts";
import type { LoadedWorld } from "../world/load-world.ts";
import { TerrainManager } from "../world/terrain-manager.ts";
import { applyAtmosphere, applyMandingaLook, createAtmosphereUniforms, sampleAtmosphere, type AtmosphereSample, type AtmosphereUniforms } from "./atmosphere.ts";
import { createHeightTexture, createSea, type Sea } from "./shaders/sea.ts";
import { createFoliageMaterial, NEAR_FADE_FLOOR, withNearFade } from "./shaders/foliage.ts";
import { withRole } from "./shaders/surface.ts";
import { GrassMask } from "./grass-mask.ts";
import { createGrass, type Grass } from "./shaders/grass.ts";
import type { PostFx } from "./post/post-fx.ts";
import { QUALITY } from "./quality.ts";
import { createSky } from "./shaders/sky.ts";
import { createTerrainMaterial } from "./shaders/terrain.ts";
import { createShoreTexture } from "./shaders/surf.ts";
import { createTreasureMaterial } from "./treasure.ts";

/** Densidade base da neblina (a mesma do `createAtmosphereUniforms`). */
const BASE_FOG = 0.0042;

/**
 * Cena-base do mundo: terreno por chunks, mar, céu e atmosfera. Usada pelo visualizador de
 * depuração e pela cena de jogo, que só decidem a câmera e o que mais desenhar.
 */
export class WorldView {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(75, 1, CAMERA_NEAR, 4000);
  readonly atmosphere: AtmosphereUniforms = createAtmosphereUniforms();
  readonly terrain: TerrainManager;
  /** Recursos da ilha (árvores, rochas, veios…) por instâncias. */
  readonly resources: ResourceLayer;
  /** Adereços dos pontos de interesse (montagens do F43), com corte por distância. */
  readonly pois: PoiLayer;
  /** Enfeites do chão (F44): pedrinhas, lajes, mudas, troncos e tábuas, espalhados no worker. */
  readonly details: DetailLayer;
  /**
   * Material de folhagem (cor por vértice + vento), reaproveitado pelos drops, estruturas e o item na
   * mão: sólido até o plano near.
   */
  readonly foliage: ShaderMaterial;
  /**
   * O mesmo material com o sumiço perto da câmera (`withNearFade`), para o que o olho pode atravessar.
   * Nos recursos, só acima do olho no alto do salto comum (`NEAR_FADE_FLOOR`): o tronco em que se
   * encosta fica sólido. Nos POIs, fundidos no mundo e sem base de instância, em qualquer altura.
   */
  readonly resourceFade: ShaderMaterial;
  readonly poiFade: ShaderMaterial;
  /** Itens no chão e o item na mão (F59: papéis próprios na malha neon). */
  readonly drop: ShaderMaterial;
  readonly treasure: ShaderMaterial;
  readonly held: ShaderMaterial;
  private readonly heldBySlot = new Map<string, ShaderMaterial>();
  readonly detailFade: ShaderMaterial;
  readonly heightTexture: DataTexture;
  /** Capim apagado sob as construções do jogador (F53): a cena de jogo põe e tira peças e estações. */
  readonly grassMask: GrassMask;
  private readonly grass: Grass;
  private readonly renderer: WebGLRenderer;
  private readonly sea: Sea;
  private readonly terrainMaterial: ShaderMaterial;
  private readonly sky: Mesh;
  private readonly sample: AtmosphereSample;
  private readonly onResize = () => this.resize();
  private readonly sunShadow: SunShadow;
  private readonly motes: Points;

  constructor(renderer: WebGLRenderer, world: LoadedWorld, phase = 0.15) {
    this.renderer = renderer;
    this.terrainMaterial = createTerrainMaterial(this.atmosphere);
    this.terrain = new TerrainManager(this.scene, this.terrainMaterial, world.pool, world.bounds);
    this.heightTexture = createHeightTexture(world.heightfield.heights);
    // A arrebentação (Fase 15) corre pela distância à costa do plano.
    this.sea = createSea(this.atmosphere, this.heightTexture, createShoreTexture(world.plan));
    this.sky = createSky(this.atmosphere);
    // Drops, estruturas e o item na mão: na máscara do contorno (F51); as variantes de cenário, não.
    // Malha neon (F59): o papel de cada camada (estruturas, no original; itens no chão e na mão, vegetação, POIs e enfeites).
    this.foliage = createFoliageMaterial(this.atmosphere, { outline: true });
    this.drop = withRole(this.foliage, "interagivel");
    // Baús de saque brilhando até serem abertos (Fase 15).
    this.treasure = createTreasureMaterial(this.foliage);
    this.held = withRole(this.foliage, "arma-heroi");
    this.resourceFade = withRole(withNearFade(this.foliage, NEAR_FADE_FLOOR), "vegetacao");
    this.poiFade = withRole(withNearFade(this.foliage), "estrutura");
    this.detailFade = withRole(withNearFade(this.foliage), "vegetacao");
    this.resources = new ResourceLayer(this.scene, world.resources, this.resourceFade, { biomeAt: planBiomes(world.plan.biome) });
    this.pois = new PoiLayer(this.scene, this.poiFade, world.plan.pois, (x, z) => sampleHeight(world.heightfield, x, z), { seed: world.seed });
    // Sumiço perto em qualquer altura (agachado, o olho entra na copa de uma muda) e encolhimento na borda.
    this.details = new DetailLayer(this.scene, this.detailFade, (cx, cz) => world.pool.details(cx, cz).then((r) => r.chunk));
    this.grassMask = new GrassMask((x, z) => sampleHeight(world.heightfield, x, z));
    this.grass = createGrass(this.atmosphere, this.heightTexture, world.plan, this.grassMask.uniforms);
    this.scene.add(this.sea.mesh, this.sky, this.grass.mesh);
    // Fora da sombra do sol (G3): o céu e o mar não projetam; o capim tem a posição no shader.
    for (const o of [this.sea.mesh, this.sky, this.grass.mesh]) o.userData["noShadow"] = true;
    this.sunShadow = new SunShadow(this.atmosphere);
    // Partículas da referência (G6): poeira em volta da câmera e brilhos subindo do anel do jogador.
    this.motes = createMotes(this.atmosphere);
    this.scene.add(this.motes);
    this.sample = sampleAtmosphere(phase);
    window.addEventListener("resize", this.onResize);
    this.resize();
  }

  /** O item na mão na cor do slot de quem segura (F60: a arma acende no neon do dono). */
  heldFor(slotHex: string): ShaderMaterial {
    let material = this.heldBySlot.get(slotHex);
    if (!material) this.heldBySlot.set(slotHex, (material = withRole(this.foliage, "arma-heroi", slotHex)));
    return material;
  }

  /** Fase do dia e a Mandinga (Lua Cheia e Neblina Grossa mudam o céu e a neblina). */
  setPhase(phase: number, timeSeconds: number, mandinga = "", fogMul = 1): void {
    const sample = sampleAtmosphere(phase, this.sample);
    const fog = applyMandingaLook(sample, mandinga, MANDINGA.fog);
    applyAtmosphere(this.atmosphere, sample, timeSeconds);
    this.atmosphere.uFogDensity.value = BASE_FOG * fog * fogMul;
  }

  /**
   * Atualiza terreno, mar e céu para a câmera atual e desenha: pelo pós-processamento (F52; `dt` em s
   * e a contagem do quadro) ou, sem ele, direto no alvo atual (as sondas desenham assim).
   */
  /** Draw calls do passe da sombra do sol no último quadro (Fase 13; fora da conta da cena). */
  get shadowCalls(): number {
    return this.sunShadow.calls;
  }

  /** Triângulos do passe da sombra do sol no último quadro. */
  get shadowTriangles(): number {
    return this.sunShadow.triangles;
  }

  render(post?: PostFx, dt = 0, stats?: FrameStats): void {
    this.camera.updateMatrixWorld();
    this.sky.position.copy(this.camera.position);
    this.sea.follow(this.camera.position.x, this.camera.position.z);
    this.grass.follow(this.camera.position.x, this.camera.position.z);
    this.grass.setDensity(QUALITY.grass);
    // Mesmo com o capim desligado: a máscara fica em dia para quando ele voltar.
    this.grassMask.update(this.camera.position.x, this.camera.position.z);
    this.grassMask.flush(this.renderer);
    this.resources.update(this.camera.position.x, this.camera.position.z);
    this.pois.update(this.camera.position.x, this.camera.position.z);
    this.details.update(this.camera.position.x, this.camera.position.z);
    this.terrain.update(this.camera);
    // Sombra do sol (G3) pelo tier do pós: Alto 2048, Médio 1024, Baixo nenhuma (as sombras-blob).
    this.sunShadow.update(this.renderer, this.scene, this.camera, this.atmosphere.uLightDir.value, QUALITY.shadows ? SUN_SHADOW_SIZE[QUALITY.post.tier] : 0, QUALITY.post.tier === "alto" ? 1 : 2);
    if (post) post.render(this.scene, this.camera, dt, stats);
    else this.renderer.render(this.scene, this.camera);
  }

  /**
   * Só nos e2e (F53): pixels de capim vistos de cima em volta de (x, z); `raw`, sem as construções. As
   * estações saem em volta do ponto (o próximo quadro as refaz em volta da câmera); a contagem do
   * quadro (F52) não muda.
   */
  grassBlades(x: number, z: number, half: number, raw = false): number {
    this.grassMask.update(x, z);
    this.grassMask.flush(this.renderer);
    return this.grass.probe(this.renderer, x, z, half, raw);
  }

  /** Quem anda no capim o deita (`Grass.trample`): os atores do quadro, a altura do chão e o tempo em s. */
  trampleGrass(casters: readonly BlobCaster[], ground: (x: number, z: number) => number, time: number): void {
    this.grass.trample(casters, ground, time);
  }

  /** Só nos e2e (F51): cor média do capim visto de cima num quadrado de `half` m em volta de (x, z). */
  grassShade(x: number, z: number, half: number): { covered: number; rgb: [number, number, number] } {
    return this.grass.shade(this.renderer, x, z, half);
  }

  resize(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  /** Libera os recursos próprios do mundo (o pool de workers é do `LoadedWorld`). Idempotente. */
  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    this.terrain.dispose();
    // O material do terreno é daqui (o `TerrainManager` só o usa): sai com a vista (Gate B1).
    this.terrainMaterial.dispose();
    this.sunShadow.dispose();
    this.motes.geometry.dispose();
    (this.motes.material as ShaderMaterial).dispose();
    this.resources.dispose();
    this.pois.dispose();
    this.details.dispose();
    this.grass.dispose();
    this.grassMask.dispose();
    this.foliage.dispose();
    this.resourceFade.dispose();
    this.poiFade.dispose();
    this.drop.dispose();
    this.treasure.dispose();
    this.held.dispose();
    for (const m of this.heldBySlot.values()) m.dispose();
    this.detailFade.dispose();
    for (const mesh of [this.sea.mesh, this.sky]) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      const material = mesh.material as ShaderMaterial;
      for (const name of ["uHeights", "uShore"]) (material.uniforms[name]?.value as { dispose?: () => void } | undefined)?.dispose?.();
      material.dispose();
    }
  }
}
